import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useLocalization } from "../../context/LocalizationContext";
import { useAppContext } from "../../context/AppContext";
import { useUserData } from "../../hooks/useUserData";
import Header from "../../containers/Header";
import AssessmentQuestion from "../../containers/AssessmentQuestion";
import CategoryTab from "../../containers/CategoryTab";
import { type AssessmentCategory } from "../../containers/CategoryTab";
import { type Question as QuestionDTO } from "../../dtos/questionnaire";
import Button from "../../components/shared/Button";
import Loader from "../../components/shared/Loader";
import Modal from "../../components/shared/Modal";
import BubblingAIAssistant from "../../components/BubblingAIAssistant";
import { submitQuestionnaire } from "../../utils/api";
import { logSecretly } from "../../utils/logSecretly";
import arrow from "../../assets/icons/arrow.svg";
import "./styles.css";

interface AssessmentType {
  id: number;
  name: string;
  title: string;
  questionCount: number;
  questions: QuestionDTO[];
}

const AssessmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type: typeParam } = useParams<{ type: string }>();
  const { t } = useLocalization();
  const assessmentFromState = location.state?.assessment as AssessmentType | undefined;
  
  const {
    completedCategories,
    currentCategory,
    setCompletedCategories,
    categories,
    setCurrentCategory,
    savedAnswers,
    saveAnswer,
    loadCategories,
    isLoadingCategories,
    markStepCompleted,
    assessmentProgress,
    setAssessmentProgress,
  } = useAppContext();

  // Prefer live categories data (which updates on language change) over stale location.state
  const assessmentFromCategories = typeParam
    ? categories?.find((c: AssessmentCategory) => String(c.id) === String(typeParam))
    : undefined;
  const assessment: AssessmentType | undefined =
    assessmentFromCategories || assessmentFromState;

  const { userData } = useUserData();
  const currentQuestionIndex = assessment ? (assessmentProgress[assessment.id] ?? 0) : 0;
  const [answers, setAnswers] = useState<Record<number, number | number[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitErrorModal, setShowSubmitErrorModal] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>("");

  const setCurrentQuestionIndex = (updater: (prev: number) => number) => {
    const newIndex = updater(currentQuestionIndex);
    if (assessment) {
      setAssessmentProgress(assessment.id, newIndex);
    }
  };

  useEffect(() => {
    if (categories.length === 0 && !isLoadingCategories) {
      loadCategories();
    }
  }, [categories.length, isLoadingCategories, loadCategories]);

  useEffect(() => {
    if (assessment && savedAnswers[assessment.id]) {
      setAnswers(savedAnswers[assessment.id] as Record<number, number | number[]>);
    }
  }, [assessment, savedAnswers]);

  const handleAnswer = (questionId: number, answer: number | number[]) => {
    const newAnswers = {
      ...answers,
      [questionId]: answer,
    };
    setAnswers(newAnswers);
    if (assessment) {
      saveAnswer(assessment.id, questionId, answer);
    }
  };


  const handleNext = () => {
    if (!assessment) return;
    if (currentQuestionIndex < assessment.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    if (!assessment || isSubmitting) return;

    try {
      setIsSubmitting(true);

      const formattedAnswers: { questionId: number; selectedOptionIds?: number[]; selectedOptionId?: number }[] = [];
      Object.entries(answers).forEach(([questionId, selectedOptions]) => {
        if (Array.isArray(selectedOptions)) {
          formattedAnswers.push({
            questionId: parseInt(questionId, 10),
            selectedOptionIds: selectedOptions,
          });
        } else {
          formattedAnswers.push({
            questionId: parseInt(questionId, 10),
            selectedOptionId: selectedOptions,
          });
        }
      });
      const submissionData = {
        questionnaireId: assessment.id,
        patientId:userData?.patientId,
        answers: formattedAnswers,
      };

      await submitQuestionnaire(submissionData);

      const updatedCompletedCategories = [...completedCategories, assessment.id];
      setCompletedCategories(updatedCompletedCategories);

      const nextCategory = categories.find(
        (cat: AssessmentCategory) => !updatedCompletedCategories.includes(cat.id)
      );

      if (nextCategory) {
        navigate(`/assessment/${nextCategory.id}`);
      } else {
        markStepCompleted("assessment");
        navigate("/dashboard");
      }
    } catch (err) {
      logSecretly("Error submitting assessment:", err);
      if (!navigator.onLine) {
        setSubmitErrorMessage(t("assessment.errorNoConnection"));
      } else if (err instanceof Error && err.message?.includes("Failed to fetch")) {
        setSubmitErrorMessage(t("assessment.errorConnection"));
      } else {
        setSubmitErrorMessage(t("assessment.errorSubmit"));
      }
      setShowSubmitErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!assessment) {
    if (isLoadingCategories) {
      return (
        <div className="page-container scrollable-page">
          <Loader text={t("loadingAssessments")} />
        </div>
      );
    }
    return null;
  }

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === assessment.questions.length - 1;

  const hasAnswer = (() => {
    const answer = answers[currentQuestion?.id];
    if (answer === undefined || answer === null) return false;
    if (Array.isArray(answer)) return answer.length > 0;
    return true;
  })();

  return (
    <div className="page-container scrollable-page">
      <Header
        title={t("assessment.title")}
        statusText={`${t("loggedInAs")} ${userData?.name || ""}`.trim()}
        showHomeButton
        showSettingsButton
      />
      <div className="assessment-detail-content">
        <div className="category-tabs">
          {categories.map((category: AssessmentCategory) => (
            <CategoryTab
              key={category.id}
              category={category}
              categories={categories}
              currentCategory={currentCategory}
              completedCategories={completedCategories}
              savedAnswers={savedAnswers}
              setCurrentCategory={setCurrentCategory}
              navigate={navigate}
            />
          ))}
        </div>

        {currentQuestion && (
          <AssessmentQuestion
            question={currentQuestion}
            questionIndex={currentQuestionIndex}
            totalQuestions={assessment.questions.length}
            onAnswer={handleAnswer}
            currentAnswer={answers[currentQuestion.id]}
          />
        )}

        <div className="assessment-footer">
          <div className="footer-btn-container">
            <Button
              variant="secondary"
              className="footer-btn next-btn"
              icon={arrow}
              iconPosition="start"
              iconStyle={{ transform: "rotate(180deg)" }}
              onClick={handlePrevious}
              disabled={isFirstQuestion}
            >
              <span>{t("previous")}</span>
            </Button>

            <Button
              variant={hasAnswer ? "solid" : "secondary"}
              className="footer-btn next-btn"
              icon={!isLastQuestion && !isSubmitting ? arrow : undefined}
              iconPosition="end"
              onClick={handleNext}
              disabled={!hasAnswer || isSubmitting}
            >
              <span>
                {isSubmitting ? t("submitting") : isLastQuestion ? t("complete") : t("next")}
              </span>
            </Button>
          </div>
        </div>
      </div>
      <Modal
        isOpen={showSubmitErrorModal}
        onClose={() => { setShowSubmitErrorModal(false); setSubmitErrorMessage(""); }}
        title={t("error")}
        message={submitErrorMessage}
      />
      <BubblingAIAssistant />
    </div>
  );
};

export default AssessmentDetail;
